import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, ChevronLeft, ChevronRight, Calendar, Bell, BellRing, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { usePeriod } from "@/hooks/usePeriod";
import { 
  requestNotificationPermission, 
  useReminderNotifications,
  testNotificationSound,
  stopAllAlarms,
  REMINDER_BEFORE_OPTIONS,
  RECURRENCE_OPTIONS 
} from "@/hooks/useReminders";

// Import refactored components
import {
  CalendarGrid,
  DayDetails,
  WeeklyView,
  UpcomingReminders,
  UpcomingAppointments
} from "@/components/calendar";

const CalendarPage = () => {
  const { periodStartDate } = usePeriod();
  const [appointments, setAppointments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [newApt, setNewApt] = useState({ title: "", date: "", time: "", description: "" });
  const [newReminder, setNewReminder] = useState({
    title: "", datetime_iso: "", description: "", reminder_before: 15,
    is_recurring: false, recurrence_type: null, recurrence_end_date: ""
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("appointments");

  useReminderNotifications(notificationsEnabled);

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;

  // Memoized computations
  const upcomingReminders = useMemo(() => {
    const now = new Date().toISOString();
    return reminders
      .filter(r => !r.is_completed && r.datetime_iso >= now)
      .sort((a, b) => a.datetime_iso.localeCompare(b.datetime_iso))
      .slice(0, 10);
  }, [reminders]);

  const upcomingAppointments = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return appointments
      .filter(a => a.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || ""))
      .slice(0, 10);
  }, [appointments]);

  // Week navigation
  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeekStart);
    newWeek.setDate(newWeek.getDate() + (direction * 7));
    setCurrentWeekStart(newWeek);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const thisMonday = new Date(today);
    thisMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    thisMonday.setHours(0, 0, 0, 0);
    return currentWeekStart.getTime() === thisMonday.getTime();
  };

  // Effects
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchReminders();
  }, [currentMonth]);

  // API calls
  const fetchAppointments = async () => {
    setLoading(true);
    const startDate = `${monthStr}-01`;
    const endDate = `${monthStr}-${daysInMonth}`;
    try {
      const response = await api.get(`/appointments`, { params: { start_date: startDate, end_date: endDate } });
      setAppointments(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get(`/reminders`);
      setReminders(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching reminders:", error);
    }
  };

  // Handlers
  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setNotificationsEnabled(true);
      toast.success("Notifiche abilitate!");
    } else {
      toast.error("Notifiche non abilitate. Controlla le impostazioni del browser.");
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    if (!newApt.title || !newApt.date) {
      toast.error("Inserisci titolo e data");
      return;
    }
    try {
      if (editingApt) {
        await api.put(`/appointments/${editingApt.appointment_id}`, newApt);
        toast.success("Appuntamento aggiornato!");
      } else {
        await api.post(`/appointments`, newApt);
        toast.success("Appuntamento aggiunto!");
      }
      setNewApt({ title: "", date: "", time: "", description: "" });
      setEditingApt(null);
      setDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.title || !newReminder.datetime_iso) {
      toast.error("Inserisci titolo e data/ora");
      return;
    }
    try {
      const localDate = new Date(newReminder.datetime_iso);
      const isoDateTime = localDate.toISOString();
      
      const reminderData = {
        ...newReminder,
        datetime_iso: isoDateTime,
        recurrence_type: newReminder.is_recurring ? newReminder.recurrence_type : null,
        recurrence_end_date: newReminder.is_recurring ? newReminder.recurrence_end_date : null
      };

      if (editingReminder) {
        await api.put(`/reminders/${editingReminder.reminder_id}`, reminderData);
        toast.success("Promemoria aggiornato!");
      } else {
        await api.post(`/reminders`, reminderData);
        toast.success("Promemoria creato!");
      }
      resetReminderForm();
      setReminderDialogOpen(false);
      fetchReminders();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const resetReminderForm = () => {
    setNewReminder({
      title: "", datetime_iso: "", description: "", reminder_before: 15,
      is_recurring: false, recurrence_type: null, recurrence_end_date: ""
    });
    setEditingReminder(null);
  };

  const handleDeleteAppointment = async (aptId) => {
    try {
      await api.delete(`/appointments/${aptId}`);
      toast.success("Appuntamento eliminato");
      fetchAppointments();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await api.delete(`/reminders/${reminderId}`);
      toast.success("Promemoria eliminato");
      fetchReminders();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/complete`);
      toast.success("Promemoria completato");
      fetchReminders();
    } catch (error) {
      toast.error("Errore nel completamento");
    }
  };

  const handleEditAppointment = (apt) => {
    setEditingApt(apt);
    setNewApt({ title: apt.title, date: apt.date, time: apt.time || "", description: apt.description || "" });
    setDialogOpen(true);
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setNewReminder({
      title: reminder.title, datetime_iso: reminder.datetime_iso, description: reminder.description || "",
      reminder_before: reminder.reminder_before || 15, is_recurring: reminder.is_recurring || false,
      recurrence_type: reminder.recurrence_type || null, recurrence_end_date: reminder.recurrence_end_date || ""
    });
    setReminderDialogOpen(true);
  };

  const handleOpenNewDialog = (date = null) => {
    setEditingApt(null);
    setNewApt({ title: "", date: date || selectedDate, time: "", description: "" });
    setDialogOpen(true);
  };

  const handleOpenNewReminderDialog = (date = null) => {
    resetReminderForm();
    if (date) {
      const now = new Date();
      const dateTime = `${date}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setNewReminder(prev => ({ ...prev, datetime_iso: dateTime }));
    }
    setReminderDialogOpen(true);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const formatMonth = () => currentMonth.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="space-y-8 animate-fade-in" data-testid="calendar-page">
      {/* Period Banner */}
      {periodStartDate && (
        <div className="bg-muted/50 border border-border rounded-sm p-3 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Visualizzando dati dal <span className="font-medium text-foreground">{new Date(periodStartDate).toLocaleDateString('it-IT')}</span>
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="page-title">Calendario</h1>
          <p className="page-subtitle">Appuntamenti e promemoria</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Notification Toggle */}
          {!notificationsEnabled ? (
            <Button variant="outline" onClick={handleEnableNotifications} className="rounded-sm" data-testid="enable-notifications-btn">
              <Bell className="w-4 h-4 mr-2" /> Abilita Notifiche
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700">
                <BellRing className="w-4 h-4" /> Notifiche attive
              </div>
              <Button variant="outline" size="sm" onClick={testNotificationSound} className="rounded-sm" title="Testa audio notifica (3 suoni)">
                <Volume2 className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={stopAllAlarms} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" title="Interrompi tutti gli allarmi attivi">
                INTERROMPI
              </Button>
            </div>
          )}
          
          {/* Add Appointment Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenNewDialog()} data-testid="add-appointment-btn" className="rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> Appuntamento
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingApt ? "Modifica Appuntamento" : "Nuovo Appuntamento"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddAppointment} className="space-y-4 pt-4">
                <div>
                  <label className="text-sm text-muted-foreground">Titolo</label>
                  <Input data-testid="apt-title-input" value={newApt.title} onChange={(e) => setNewApt({ ...newApt, title: e.target.value })} placeholder="Es: Visita medica" className="rounded-sm mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Data</label>
                    <Input data-testid="apt-date-input" type="date" value={newApt.date} onChange={(e) => setNewApt({ ...newApt, date: e.target.value })} className="rounded-sm font-mono mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Ora (opzionale)</label>
                    <Input data-testid="apt-time-input" type="time" value={newApt.time} onChange={(e) => setNewApt({ ...newApt, time: e.target.value })} className="rounded-sm font-mono mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Descrizione (opzionale)</label>
                  <Textarea data-testid="apt-desc-input" value={newApt.description} onChange={(e) => setNewApt({ ...newApt, description: e.target.value })} placeholder="Dettagli aggiuntivi..." className="rounded-sm mt-1" />
                </div>
                <Button type="submit" data-testid="save-apt-btn" className="w-full rounded-sm">{editingApt ? "Aggiorna" : "Aggiungi"}</Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Reminder Dialog */}
          <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenNewReminderDialog()} data-testid="add-reminder-btn" variant="outline" className="rounded-sm">
                <Bell className="w-4 h-4 mr-2" /> Promemoria
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingReminder ? "Modifica Promemoria" : "Nuovo Promemoria"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddReminder} className="space-y-4 pt-4">
                <div>
                  <label className="text-sm text-muted-foreground">Titolo</label>
                  <Input data-testid="reminder-title-input" value={newReminder.title} onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })} placeholder="Es: Prendere medicina" className="rounded-sm mt-1" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Data e Ora</label>
                  <Input data-testid="reminder-datetime-input" type="datetime-local" value={newReminder.datetime_iso} onChange={(e) => setNewReminder({ ...newReminder, datetime_iso: e.target.value })} className="rounded-sm font-mono mt-1" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Avvisami</label>
                  <Select value={String(newReminder.reminder_before)} onValueChange={(value) => setNewReminder({ ...newReminder, reminder_before: parseInt(value) })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="reminder-before-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REMINDER_BEFORE_OPTIONS.map(opt => <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Descrizione (opzionale)</label>
                  <Textarea data-testid="reminder-desc-input" value={newReminder.description} onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })} placeholder="Dettagli aggiuntivi..." className="rounded-sm mt-1" />
                </div>
                
                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <p className="text-sm font-medium">Promemoria ricorrente</p>
                    <p className="text-xs text-muted-foreground">Ripeti automaticamente</p>
                  </div>
                  <Switch checked={newReminder.is_recurring} onCheckedChange={(checked) => setNewReminder({ ...newReminder, is_recurring: checked })} data-testid="reminder-recurring-switch" />
                </div>
                
                {newReminder.is_recurring && (
                  <div className="space-y-3 p-3 bg-muted/50 rounded-sm">
                    <div>
                      <label className="text-sm text-muted-foreground">Frequenza</label>
                      <Select value={newReminder.recurrence_type || ""} onValueChange={(value) => setNewReminder({ ...newReminder, recurrence_type: value })}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="recurrence-type-select"><SelectValue placeholder="Seleziona frequenza" /></SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Termina il (opzionale)</label>
                      <Input type="date" value={newReminder.recurrence_end_date} onChange={(e) => setNewReminder({ ...newReminder, recurrence_end_date: e.target.value })} className="rounded-sm font-mono mt-1" data-testid="recurrence-end-input" />
                    </div>
                  </div>
                )}
                
                <Button type="submit" data-testid="save-reminder-btn" className="w-full rounded-sm">{editingReminder ? "Aggiorna" : "Crea Promemoria"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Mese prec.</span>
        </Button>
        <span className="font-heading text-sm sm:text-lg capitalize">{formatMonth()}</span>
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
          <span className="hidden sm:inline mr-1">Mese succ.</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <CalendarGrid
          year={year}
          month={month}
          monthStr={monthStr}
          daysInMonth={daysInMonth}
          firstDayOfWeek={firstDayOfWeek}
          selectedDate={selectedDate}
          appointments={appointments}
          reminders={reminders}
          onSelectDate={setSelectedDate}
          onDoubleClick={handleOpenNewDialog}
        />

        <DayDetails
          selectedDate={selectedDate}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          appointments={appointments}
          reminders={reminders}
          loading={loading}
          onEditAppointment={handleEditAppointment}
          onDeleteAppointment={handleDeleteAppointment}
          onAddAppointment={handleOpenNewDialog}
          onEditReminder={handleEditReminder}
          onDeleteReminder={handleDeleteReminder}
          onCompleteReminder={handleCompleteReminder}
          onAddReminder={handleOpenNewReminderDialog}
        />
      </div>

      <WeeklyView
        currentWeekStart={currentWeekStart}
        reminders={reminders}
        onNavigateWeek={navigateWeek}
        goToCurrentWeek={goToCurrentWeek}
        isCurrentWeek={isCurrentWeek}
        onEditReminder={handleEditReminder}
      />

      <UpcomingReminders
        reminders={upcomingReminders}
        onComplete={handleCompleteReminder}
        onEdit={handleEditReminder}
        onDelete={handleDeleteReminder}
      />

      <UpcomingAppointments
        appointments={upcomingAppointments}
        onEdit={handleEditAppointment}
        onDelete={handleDeleteAppointment}
      />
    </div>
  );
};

export default CalendarPage;
